/**
 * Candidate strategies for canonicalizing host names into DNS labels, for the
 * Managed Internal DNS milestone's preview UI. Exploration code behind the
 * MANAGED_DNS flag — not the shipping canonicalizer.
 *
 * Return conventions: `''` means the name canonicalizes to nothing usable;
 * `null` means the name was rejected outright — the IDNA machinery refused it,
 * or its encoded form overflows a DNS label.
 */

const APOSTROPHES = /['‘’]/g;

/**
 * DNS labels max out at 63 octets; every label reaching here is ASCII, so
 * length equals octets. Re-trim in case the cut lands after a dash.
 */
function clamp63(label: string): string {
  return label.slice(0, 63).replace(/-+$/, '');
}

/** Lowercase, collapse every non-[a-z0-9] run to a single dash, trim edge dashes. */
export function dashRuns(name: string): string {
  const label = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return clamp63(label);
}

/** NFKD-fold accents to ASCII and delete apostrophes, then apply dash-runs. */
export function foldDrop(name: string): string {
  const folded = name.normalize('NFKD').replace(/[̀-ͯ]/g, '');
  return dashRuns(folded.replace(APOSTROPHES, ''));
}

/**
 * Keep letters/digits/emoji from any script, dash the rest, then run the
 * result through the URL parser for real IDNA/UTS-46 (punycode) handling.
 */
export function foldPunycode(name: string): string | null {
  // NFKC up front mirrors UTS-46 mapping, so fullwidth digits (２０２４)
  // reach the ASCII fast-path below as plain digits.
  const kept = name
    .normalize('NFKC')
    .toLowerCase()
    .replace(APOSTROPHES, '')
    .replace(/[^\p{L}\p{N}\p{Extended_Pictographic}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
  if (!kept) {
    return '';
  }
  // The WHATWG URL parser reads numeric hostnames as IPv4 numbers
  // (`2024` → `0.0.7.232`, `0x1a` → `0.0.0.26`), so names that are already
  // plain ASCII labels skip the IDNA round-trip entirely.
  if (/^[a-z0-9-]+$/.test(kept)) {
    return clamp63(kept);
  }
  try {
    const label = new URL(`http://${kept}`).hostname;
    // Truncating punycode corrupts the encoding, so an over-long label is
    // rejected rather than clamped.
    return label.length > 63 ? null : label;
  } catch {
    return null;
  }
}

export type StrategyID = 'dash-runs' | 'fold-drop' | 'fold-punycode';

export type Strategy = {
  id: StrategyID;
  label: string;
  description: string;
  canonicalize: (name: string) => string | null;
};

export const STRATEGIES: Strategy[] = [
  {
    id: 'dash-runs',
    label: 'Dash runs',
    description: 'Lowercase, collapse anything that isn’t a-z or 0-9 into a single dash.',
    canonicalize: dashRuns,
  },
  {
    id: 'fold-drop',
    label: 'Fold accents + drop apostrophes',
    description:
      'Fold accented letters to plain ASCII (café → cafe) and delete apostrophes instead of dashing them, then dash runs.',
    canonicalize: foldDrop,
  },
  {
    id: 'fold-punycode',
    label: 'Keep scripts + punycode',
    description:
      'Keep letters from any script and emoji, then encode non-ASCII with punycode (IDNA) the way browsers do.',
    canonicalize: foldPunycode,
  },
];
